<template>
  <section :class="$style.section">
    <h2 :class="$style.title">Персональные данные</h2>

    <div :class="$style.fields">
      <div :class="$style.field">
        <div
          :class="[
            $style.control,
            { [$style.active]: hasPhoneValue },
          ]"
        >
          <input
            ref="phoneInput"
            :value="clientPhoneInputValue"
            type="text"
            placeholder="Ваш телефон"
            :class="[
              { [$style.invalid]: phoneError },
              { [$style.withClear]: showFieldClear('phone') },
            ]"
            @focus="onPhoneFocus"
            @input="onPhoneInput"
            @blur="onPhoneBlur"
            @click="clampPhoneCaret"
            @keyup="clampPhoneCaret"
            @select="clampPhoneCaret"
            @keydown.enter.prevent="onPhoneEnter"
          />
          <button
            v-if="showFieldClear('phone')"
            type="button"
            :class="$style.clear"
            aria-label="Очистить телефон"
            @mousedown.prevent
            @click="clearPersonalField('phone')"
          />
        </div>
        <div v-if="phoneError" :class="$style.error">{{ phoneError }}</div>
        <div
          v-if="clients.length && activeField === 'phone'"
          :class="$style.hints"
        >
          <button type="button" :class="$style.closeHints" @click="resetClients">
            ✕
          </button>
          <button
            v-for="item in clients"
            :key="item.id"
            type="button"
            :class="$style.hint"
            @click="handleClientSelect(item)"
          >
            {{ item.name }} {{ item.phone_number }}
          </button>
        </div>
      </div>

      <div :class="$style.field">
        <div
          :class="[
            $style.control,
            { [$style.active]: order.client_data?.name?.trim() },
          ]"
        >
          <input
            :value="order.client_data?.name || ''"
            type="text"
            placeholder="Ваше имя"
            :class="[
              { [$style.invalid]: nameError },
              { [$style.withClear]: showFieldClear('name') },
            ]"
            @input="handleNameInput"
          />
          <button
            v-if="showFieldClear('name')"
            type="button"
            :class="$style.clear"
            aria-label="Очистить имя"
            @mousedown.prevent
            @click="clearPersonalField('name')"
          />
        </div>
        <div v-if="nameError" :class="$style.error">{{ nameError }}</div>
      </div>

      <div :class="$style.field">
        <div
          :class="[
            $style.control,
            {
              [$style.active]: carNumberDisplay || isCarNumberFocused,
            },
          ]"
        >
          <input
            ref="carNumberInput"
            :value="carNumberDisplay"
            type="text"
            placeholder="Регистрационный номер автомобиля"
            :class="[
              { [$style.invalid]: carNumberError },
              { [$style.withClear]: showFieldClear('car_number') },
            ]"
            @focus="onCarNumberFocus"
            @input="onCarNumberInput"
            @blur="onCarNumberBlur"
          />
          <button
            v-if="showFieldClear('car_number')"
            type="button"
            :class="$style.clear"
            aria-label="Очистить регистрационный номер"
            @mousedown.prevent
            @click="clearPersonalField('car_number')"
          />
        </div>
        <div v-if="carNumberHint && !carNumberError" :class="$style.helper">
          {{ carNumberHint }}
        </div>
        <div v-if="carNumberError" :class="$style.error">
          {{ carNumberError }}
        </div>
        <div v-if="clientCarHints.length" :class="$style.carHints">
          <button
            v-for="item in clientCarHints"
            :key="`${item.label}-${item.index}`"
            type="button"
            :class="$style.carHint"
            @click="selectClientCar(item.car)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from "vuex";
import clientSearchMixin from "@/mixins/clientSearchMixin";
import { getFirstError } from "@/utils/validation";

export default {
  name: "PersonalDataSection",
  emits: ["client-selected", "dirty", "persisted"],
  mixins: [clientSearchMixin],
  data() {
    return {
      isPhoneFocused: false,
      phoneInputDraft: null,
      isCarNumberFocused: false,
    };
  },
  computed: {
    ...mapState({
      order: (state) => state.orders.order,
      errors: (state) => state.orders.errors,
    }),
    clientPhoneInputValue() {
      if (this.phoneInputDraft !== null) {
        return this.phoneInputDraft;
      }

      if (!this.order.client_data?.phone_number) {
        return "+7";
      }

      return this.normalizeClientPhoneInput(
        this.order.client_data?.phone_number,
      );
    },
    hasPhoneValue() {
      return Boolean(this.order.client_data?.phone_number);
    },
    nameError() {
      return (
        this.getFieldError(["client_data.name", "name"]) ||
        (!this.order.client_data?.name?.trim()
          ? this.getFieldError(["client_data"])
          : "")
      );
    },
    phoneError() {
      return (
        this.getFieldError(["client_data.phone_number", "phone_number"]) ||
        (!this.order.client_data?.phone_number?.trim()
          ? this.getFieldError(["client_data"])
          : "")
      );
    },
    carNumberError() {
      return this.getFieldError(["car.number", "cars.0.number"]);
    },
    clientCars() {
      return this.getActiveClientCars(this.client);
    },
    hasCarNumberValue() {
      return Boolean(this.carNumberDisplay);
    },
    filledPersonalFields() {
      return [
        this.hasPhoneValue,
        Boolean(this.order.client_data?.name?.trim()),
        this.hasCarNumberValue,
      ].filter(Boolean).length;
    },
    clientCarHints() {
      const cars = this.clientCars;
      if (cars.length <= 1) {
        return [];
      }

      const selectedIndex = this.findSelectedClientCarIndex(cars, this.order.car);

      return cars.reduce((hints, car, index) => {
        if (index === selectedIndex) {
          return hints;
        }

        const label = this.formatClientCarOption(car);
        if (!label) {
          return hints;
        }

        hints.push({ car, index, label });
        return hints;
      }, []);
    },
    carNumberDisplay() {
      if (!this.isEmptyCarNumber(this.order.car?.number)) {
        return this.order.car.number;
      }

      return this.order.car?.make ? this.getCarNumberFieldValue("") : "";
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.phoneInput?.focus();
    });
  },
  methods: {
    getFieldError(paths) {
      return getFirstError(this.errors, paths);
    },
    getPhoneCaretMinPosition(value = this.clientPhoneInputValue) {
      const phoneValue = String(value || "");
      if (phoneValue.startsWith("+7")) {
        return 2;
      }
      if (phoneValue.startsWith("+")) {
        return 1;
      }
      return 0;
    },
    clampPhoneCaret(event) {
      const input = event?.target || this.$refs.phoneInput;
      if (!input || typeof input.setSelectionRange !== "function") {
        return;
      }

      const minPosition = this.getPhoneCaretMinPosition(input.value);
      const selectionStart = input.selectionStart ?? minPosition;
      const selectionEnd = input.selectionEnd ?? minPosition;

      if (selectionStart >= minPosition && selectionEnd >= minPosition) {
        return;
      }

      const nextPosition = Math.max(selectionEnd, minPosition);
      input.setSelectionRange(nextPosition, nextPosition);
    },
    handleNameInput(event) {
      this.$store.commit("orders/setOrderField", {
        field: "client_data.name",
        value: event.target.value,
      });
      this.formatClientName();
      this.$emit("dirty");
    },
    onPhoneInput(event) {
      const prevPhone = this.order.client_data?.phone_number;
      const normalizedPhone = this.handleClientPhoneInput(event);
      this.phoneInputDraft = normalizedPhone;
      this.clampPhoneCaret(event);
      this.$emit("dirty");

      const nextPhone = this.order.client_data?.phone_number;
      if (
        this.isRuPhoneMaskComplete(nextPhone) &&
        (nextPhone !== prevPhone || !this.hasSelectedClient())
      ) {
        this.triggerClientSearch("phone");
      }
    },
    onPhoneFocus() {
      this.isPhoneFocused = true;
      this.phoneInputDraft = this.order.client_data?.phone_number
        ? this.normalizeClientPhoneInput(this.order.client_data.phone_number)
        : "+7";
      this.setClientPhoneInputState(this.phoneInputDraft);
      this.$nextTick(() => this.clampPhoneCaret());
    },
    onPhoneBlur() {
      this.isPhoneFocused = false;
      this.phoneInputDraft = null;
      if (!this.isRuClientPhone(this.order.client_data?.phone_number)) {
        this.triggerClientSearch("phone");
      }
    },
    onPhoneEnter() {
      if (!this.isRuClientPhone(this.order.client_data?.phone_number)) {
        this.triggerClientSearch("phone");
      }
    },
    showFieldClear(field) {
      if (this.filledPersonalFields < 2) {
        return false;
      }

      if (field === "phone") {
        return this.hasPhoneValue;
      }
      if (field === "name") {
        return Boolean(this.order.client_data?.name?.trim());
      }
      if (field === "car_number") {
        return this.hasCarNumberValue;
      }
      return false;
    },
    clearPersonalField(field) {
      if (field === "phone") {
        this.phoneInputDraft = null;
        this.setClientPhoneInputState("+7");
        this.clearClientField("phone");
        this.$emit("client-selected", null);
      } else if (field === "name") {
        this.$store.commit("orders/setOrderField", {
          field: "client_data.name",
          value: "",
        });
      } else if (field === "car_number") {
        this.clearSelectedCar();
      }

      this.$emit("dirty");
    },
    clearSelectedCar() {
      [
        "car.number",
        "car.make",
        "car.model",
        "car.generation",
        "car.tires.summer_tires.first_tires",
        "car.tires.first_tires",
        "car.tires.summer_tires.second_tires",
        "car.tires.second_tires",
      ].forEach((field) => {
        this.$store.commit("orders/setOrderField", { field, value: null });
      });
      ["make", "model", "generation", "first_tires", "second_tires"].forEach(
        (field) => {
          this.$store.commit("orders/setCarField", { field, value: "" });
        },
      );
      this.carNumberHint = "";
    },
    onCarNumberFocus() {
      this.isCarNumberFocused = true;
    },
    onCarNumberBlur() {
      this.isCarNumberFocused = false;
    },
    onCarNumberInput(event) {
      this.applyCarNumberInput(event);
      this.$emit("dirty");
    },
    selectClientCar(car) {
      this.applyClientCarIdentity(car);
      this.$emit("dirty");
    },
    async handleClientSelect(client) {
      await this.setClient(client, { searchField: this.activeField });
    },
    onStepOnePersisted() {
      this.$emit("persisted");
    },
    onStepOneChanged() {
      this.$emit("dirty");
    },
  },
};
</script>

<style lang="scss" module>
.section {
  padding: 1.5rem 1.5rem 0;
  @include tablet {
    padding: 1rem;
  }
}

.title {
  font-size: 1.3125rem;
  font-weight: 600;
  margin: 0 0 1.5rem;
  line-height: 1.4;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
}

.control {
  position: relative;
  display: flex;
  align-items: center;
  background-color: $bg-primary;
  border: 1px solid $bg-primary;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  min-height: 3rem;

  &.active {
    background-color: $bg-white;
    border-color: $bg-secondary;
  }
}

input {
  flex: 1 1 auto;
  border: none;
  background: transparent;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: $t-primary;
  min-width: 0;

  &::placeholder {
    color: $t-tertiary;
    font-weight: 400;
    font-size: 0.875rem;
    opacity: 1;
  }

  &.invalid {
    color: $red-primary;
  }

  &.withClear {
    padding-right: 2.5rem;
  }
}

.clear {
  position: absolute;
  top: 50%;
  right: 1rem;
  width: 1rem;
  height: 1rem;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  transform: translateY(-50%);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1rem;
    height: 2px;
    border-radius: 999px;
    background-color: $t-primary;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}

.error,
.helper {
  left: 1rem;
  font-size: 0.75rem;
  line-height: 1.3;
}

.error {
  color: $red-primary;
}

.helper {
  color: $t-secondary;
}

.hints {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: $bg-white;
  border: 1px solid $bg-secondary;
  border-radius: 0.25rem;
  overflow: hidden;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
}

.closeHints {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  color: $t-secondary;
  z-index: 1;
}

.hint {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  color: $t-secondary;
  cursor: pointer;
  border-bottom: 1px solid $bg-primary;

  &:last-child {
    border-bottom: none;
  }
}

.carHints {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.carHint {
  border: none;
  background-color: $blue;
  color: #2c2c2c;
  border-radius: 0.25rem;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
